import { axios } from "@cores/axiosWrapper";
import queryString from "@cores/queryString";

/**
 * gen-ai subscription-manager API (DAM 게이트웨이 프록시).
 *
 * DAM 게이트웨이가 `/gen-ai/*` 로 mzc-dam-gen-ai(marketplace entitlements)의
 * jobs/active-count · subscriptions/me · subscriptions/me/usages 를 프록시로 노출한다.
 * 서버는 `projectId` 헤더로 Gen AI TenantId 를 해석해 upstream 을 호출하므로,
 * 별도 테넌트 인증(x-api-key/x-tenant-id) 없이 DAM Bearer(axios 가 자동 주입) +
 * stageId/projectId 헤더만으로 호출한다. (ai-workspace.js 의 gen-ai 호출 관례와 동일)
 *
 * `customErrorMessage: true` 를 모든 호출에 붙이는 이유:
 * /subscriptions/me 의 404 는 "활성 구독 없음"이라는 정상 상태인데, 플래그가 없으면
 * 전역 인터셉터가 에러 스낵바를 띄운다. 에러는 전부 호출부(Redux thunk → 페이지)에서
 * 인라인으로 처리한다.
 */

/**
 * 현재 테넌트의 활성 구독 정보 조회.
 * 응답: { plan(nullable {id,name}), subscribedAt, planStartedAt(nullable),
 *        maxConcurrentJobs(nullable=무제한), monthlyFee(nullable) }
 * 활성 구독이 없으면 404 를 반환한다(에러가 아니라 정상 상태).
 */
export const getSubscriptionAPI = (stageId, apiEndpoint, projectId) => {
    return axios.get(`${apiEndpoint}/gen-ai/subscriptions/me`, {
        headers: { stageId, projectId },
        customErrorMessage: true,
    });
};

/**
 * 특정 월의 사용량/사용료 조회.
 * 활성 구독이 없어도 조회 가능하며, 그 경우 월 전체가 plan:null 단일 구간으로 집계된다.
 * @param {string} stageId
 * @param {string} apiEndpoint
 * @param {string} projectId
 * @param {object} params
 * @param {string} params.month - "YYYY-MM" (필수)
 * @param {string} [params.offset] - "+09:00" 형태. encodeURIComponent 로 "%2B09%3A00" 로 인코딩된다.
 * 응답: { month, offset, periods[], totalUsageMinutes, totalUsageAmount, totalMonthlyFee, totalAmount }
 *   periods[]: { plan(nullable), rangeStart, rangeEnd, usageMinutes, usageAmount, monthlyFee }
 */
export const getSubscriptionUsagesAPI = (stageId, apiEndpoint, projectId, { month, offset }) => {
    // month(필수)/offset(선택)을 쿼리스트링으로 조립한다. offset 미지정 시 키를 빼서
    // offset=undefined 가 전송되지 않게 한다.
    const searchQueryParams = queryString.stringify({ month, ...(offset ? { offset } : {}) });
    return axios.get(`${apiEndpoint}/gen-ai/subscriptions/me/usages?${searchQueryParams}`, {
        headers: { stageId, projectId },
        customErrorMessage: true,
    });
};

/**
 * 진행 중인 AI Job 수와 한도 조회.
 * 응답: { count, limit(nullable=무제한) }
 */
export const getActiveJobCountAPI = (stageId, apiEndpoint, projectId) => {
    return axios.get(`${apiEndpoint}/gen-ai/jobs/active-count`, {
        headers: { stageId, projectId },
        customErrorMessage: true,
    });
};
